import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useMediaQuery, {
  BELOW_SIDEBAR_QUERY,
} from '@client/components/designSystem/useMediaQuery';

const FOCUSABLE = 'a[href], button:not([disabled])';

// The narrow-screen drawer. On wide screens the sidebar is always visible
// (CSS) and this never opens. While open: focus moves inside and stays there,
// Escape closes, and focus returns to the menu button on close.
function useSidebarDrawer() {
  const [isOpen, setIsOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const sidebarRef = useRef(null);
  const wasOpen = useRef(false);
  const { pathname } = useLocation();

  const focusables = () =>
    sidebarRef.current
      ? Array.from(sidebarRef.current.querySelectorAll(FOCUSABLE))
      : [];

  useEffect(() => {
    if (isOpen) {
      const [first] = focusables();
      if (first) {
        first.focus();
      }
    } else if (wasOpen.current && menuButtonRef.current) {
      menuButtonRef.current.focus();
    }
    wasOpen.current = isOpen;
  }, [isOpen]);

  function close() {
    setIsOpen(false);
  }

  // Any page change closes the drawer (links, back button, redirects).
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  // Widening the window shows the sidebar for good, so the drawer is done.
  const isDrawerMode = useMediaQuery(BELOW_SIDEBAR_QUERY);
  useEffect(() => {
    if (!isDrawerMode) {
      setIsOpen(false);
    }
  }, [isDrawerMode]);

  function handleKeyDown(event) {
    if (!isOpen) {
      return;
    }
    // A control inside (the country selector) handles its own Escape first.
    if (event.key === 'Escape' && !event.defaultPrevented) {
      close();
    } else if (event.key === 'Tab') {
      const items = focusables();
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  return {
    isOpen,
    toggle: () => setIsOpen((open) => !open),
    close,
    handleKeyDown,
    menuButtonRef,
    sidebarRef,
  };
}

export default useSidebarDrawer;
